// pages/FreebieClaimPage.tsx
import * as React from "react";
import { useParams } from "react-router-dom";
import Loader from "../Components/Loader";
import { ErrorCard } from "../Components/ErrorCard";
import { FreebieCard } from "../Components/FreebieCard";
import { SuccessCard } from "../Components/SuccessCard";
import {
  apiPublicRawGet,
  apiPublicRawPost,
} from "../DataService/apiClient";
import './FreebieClaimPage.css';


// type ClaimState =
//   | "loading"
//   | "error"
//   | "ready"
//   | "claiming"
//   | "claimed";
type ViewState = "loading" | "error" | "ready" | "claimed";

export const FreebieClaimPage = () => {
  const { token } = useParams<{ token: string }>();
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);

  const [state, setState] = React.useState<ViewState>("loading");
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // kommer från backend
  const [freebie, setFreebie] = React.useState<{
    title: string;
    description?: string;
    isAvailable: boolean;
    allowMultipleDownloadsPerEmail:boolean;
  } | null>(null);

  // claim-resultat
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");

  const getErrorMessage = async (response: Response, fallback: string) => {
    const contentType = response.headers.get("content-type") ?? "";

    if (contentType.includes("application/json")) {
      const payload = await response.json().catch(() => null);
      if (payload && typeof payload === "object") {
        const maybeMessage =
          (payload as { message?: unknown; title?: unknown; error?: unknown }).message ??
          (payload as { message?: unknown; title?: unknown; error?: unknown }).title ??
          (payload as { message?: unknown; title?: unknown; error?: unknown }).error;

        if (typeof maybeMessage === "string" && maybeMessage.trim()) {
          return maybeMessage;
        }
      }
    }

    const text = await response.text().catch(() => "");
    if (text.trim()) {
      return text.trim();
    }

    if (response.status === 410) {
      return "Länken är inte längre giltig eller har redan använts.";
    }

    if (response.status === 404) {
      return "Vi kunde inte hitta gåvan.";
    }

    return fallback;
  };

  React.useEffect(() => {
    if (!token) {
      setError("Ogiltig länk");
      setState("error");
      return;
    }

    const loadFreebie = async () => {
      try {
        const response = await apiPublicRawGet(`/api/freebies/${token}`);

        if (!response.ok) {
          throw new Error(
            await getErrorMessage(
              response,
              `Kunde inte ladda gåvan (${response.status}).`
            )
          );
        }

        const res = await response.json();

        if (!res?.isAvailable) {
          setError("Den här gåvan är inte längre tillgänglig.");
          setState("error");
          return;
        }

        setFreebie(res);
        setState("ready");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Kunde inte ladda gåvan.");
        setState("error");
      }
    };

    loadFreebie();
  }, [token]);

  const onClaim = async () => {
    if (!token) return;

    setIsSubmitting(true);

    try {
      const response = await apiPublicRawPost(`/api/freebies/${token}/claim`, {
        name,
        email,
        captchaToken: captchaToken
      });

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(
            response,
            `Kunde inte skapa nedladdningslänken (${response.status}).`
          )
        );
      }

      setState("claimed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Något gick fel när vi försökte ge dig filen.");
      setState("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="freebie-claim-page">
      <div className="freebie-claim-shell">
      {state === "loading" && <Loader loaderSize="LARGE" />}

      {state === "error" && (
        <ErrorCard message={error ?? "Något gick fel"} />
      )}

      {state === "ready" && freebie && (
        <FreebieCard
          freebie={freebie}
          name={name}
          email={email}
          onNameChange={setName}
          onEmailChange={setEmail}
          onClaim={onClaim}
          isSubmitting={isSubmitting}
          captchaToken={captchaToken}
          onCaptchaSuccess={setCaptchaToken}
          onCaptchaExpire={() => setCaptchaToken(null)}
        />
      )}

      {isSubmitting && <Loader loaderSize="MEDIUM" />}

      {state === "claimed" && (
        <SuccessCard
          title={freebie?.title ?? "gåvan"}
          singleUse={!freebie?.allowMultipleDownloadsPerEmail}
        />
      )}
      </div>
    </div>
  );
};
